import { useState } from 'react';
import { Plus } from 'lucide-react';
import useMerchantActions from '@/hooks/useMerchantActions';
import MerchantRow from './merchant/MerchantRow';
import MerchantCreateModal from './merchant/MerchantCreateModal';
import MerchantCreatedResultModal from './merchant/MerchantCreatedResultModal';
import MerchantSimpleModal from './merchant/MerchantSimpleModal';
import MerchantProjectsModal from './merchant/MerchantProjectsModal';
import MerchantConcentratorModal from './merchant/MerchantConcentratorModal';
import MerchantDeleteModal from './merchant/MerchantDeleteModal';

const EMPTY_MERCHANT = {
  email: '', password: '', company_name: '', rif: '', phone: '',
  project_name: '', project_description: '',
};
const EMPTY_CONCENTRATOR = {
  use_own_concentrator: false, rif: '', phone_p2p: '', phone_bank_code: '0138',
  cuenta_operativa: '', cuenta_bank_code: '0138', titular_name: '',
};

export default function MerchantManagementTab({ merchants, setToast, onReload }) {
  const actions = useMerchantActions({ setToast, onReload });

  // Selection (single source of truth for which merchant a modal targets).
  const [selectedMerchant, setSelectedMerchant] = useState(null);

  // Modal visibility flags.
  const [showCreate, setShowCreate] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const [showStatus, setShowStatus] = useState(false);
  const [showFee, setShowFee] = useState(false);
  const [showTolerance, setShowTolerance] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [showConcentrator, setShowConcentrator] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Form values per modal.
  const [creating, setCreating] = useState(false);
  const [newMerchant, setNewMerchant] = useState(EMPTY_MERCHANT);
  const [newStatus, setNewStatus] = useState('');
  const [newFee, setNewFee] = useState('');
  const [newTolerance, setNewTolerance] = useState('');
  const [toleranceReason, setToleranceReason] = useState('');
  const [feeReason, setFeeReason] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [concentrator, setConcentrator] = useState(EMPTY_CONCENTRATOR);
  const [savingConcentrator, setSavingConcentrator] = useState(false);

  // Delete modal state.
  const [deleteAction, setDeleteAction] = useState('soft');
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteOverride, setDeleteOverride] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ── Open handlers ──────────────────────────────────────
  const openCreate = () => { setNewMerchant(EMPTY_MERCHANT); setShowCreate(true); };
  const openProjects = (m) => { setSelectedMerchant(m); setShowProjects(true); };
  const openStatus = (m) => { setSelectedMerchant(m); setNewStatus(m.status); setShowStatus(true); };
  const openFee = (m) => { setSelectedMerchant(m); setNewFee(String(m.fee_percentage)); setShowFee(true); };
  const openTolerance = (m) => {
    setSelectedMerchant(m);
    setNewTolerance(m.overpay_tolerance_ves ? String(m.overpay_tolerance_ves) : '');
    setShowTolerance(true);
  };
  const openPassword = (m) => { setSelectedMerchant(m); setNewPassword(''); setShowPassword(true); };

  const openConcentrator = async (m) => {
    setSelectedMerchant(m);
    setShowConcentrator(true);
    const c = await actions.loadConcentrator(m.user_id);
    if (c) {
      setConcentrator({
        use_own_concentrator: !!c.use_own_concentrator,
        rif: c.rif || '',
        phone_p2p: c.phone_p2p || '',
        phone_bank_code: c.phone_bank_code || '0138',
        cuenta_operativa: c.cuenta_operativa || '',
        cuenta_bank_code: c.cuenta_bank_code || '0138',
        titular_name: c.titular_name || m.company_name || '',
      });
    }
  };

  const openDelete = async (m, action) => {
    setSelectedMerchant(m);
    setDeleteAction(action);
    setDeleteConfirmName(''); setDeleteReason(''); setDeleteOverride(false); setDeleteImpact(null);
    setShowDelete(true);
    const impact = await actions.loadDeletionImpact(m.user_id);
    if (impact) setDeleteImpact(impact);
  };

  // ── Submit handlers ────────────────────────────────────
  const submitCreate = async () => {
    setCreating(true);
    const result = await actions.createMerchant(newMerchant);
    setCreating(false);
    if (result) {
      setShowCreate(false);
      setCreatedResult(result);
    }
  };

  const submitStatus = async () => {
    if (await actions.updateStatus(selectedMerchant.user_id, newStatus)) setShowStatus(false);
  };
  const submitFee = async () => {
    if (await actions.updateFee(selectedMerchant.user_id, newFee, feeReason)) {
      setShowFee(false); setFeeReason('');
    }
  };
  const submitTolerance = async () => {
    if (await actions.updateOverpayTolerance(selectedMerchant.user_id, newTolerance, toleranceReason)) {
      setShowTolerance(false); setToleranceReason('');
    }
  };
  const submitPassword = async () => {
    if (await actions.resetPassword(selectedMerchant.user_id, newPassword)) {
      setShowPassword(false); setNewPassword('');
    }
  };
  const submitConcentrator = async () => {
    setSavingConcentrator(true);
    const ok = await actions.saveConcentrator(selectedMerchant.user_id, concentrator);
    setSavingConcentrator(false);
    if (ok) setShowConcentrator(false);
  };
  const submitDelete = async () => {
    setDeleting(true);
    const ok = await actions.deleteMerchant(selectedMerchant.user_id, {
      action: deleteAction,
      confirmation_name: deleteConfirmName,
      reason: deleteReason,
      override_blocks: deleteOverride,
    });
    setDeleting(false);
    if (ok) setShowDelete(false);
  };

  return (
    <div data-testid="merchant-management-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Gestion de Comercios</h2>
        <button
          data-testid="create-merchant-btn"
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg"
          style={{ backgroundColor: '#003594' }}
        >
          <Plus className="w-4 h-4" /> Crear Comercio
        </button>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proyectos</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comision</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tol. sobrepago</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {merchants.length === 0 ? (
              <tr><td colSpan="9" className="px-6 py-8 text-center text-gray-500">No hay comercios registrados</td></tr>
            ) : merchants.map((m) => (
              <MerchantRow
                key={m.user_id}
                merchant={m}
                sendingEmail={actions.sendingEmail === m.user_id}
                onOpenProjects={() => openProjects(m)}
                onOpenStatus={() => openStatus(m)}
                onOpenFee={() => openFee(m)}
                onOpenTolerance={() => openTolerance(m)}
                onOpenPassword={() => openPassword(m)}
                onOpenConcentrator={() => openConcentrator(m)}
                onDownloadCredentials={(fmt) => actions.downloadCredentials(m.user_id, fmt)}
                onResendEmail={() => actions.resendEmail(m.user_id, m.email)}
                onDownloadGuide={(fmt) => actions.downloadGuide(m.user_id, fmt)}
                onOpenDelete={() => openDelete(m, 'soft')}
                onOpenPurge={() => openDelete(m, 'purge')}
              />
            ))}
          </tbody>
        </table>
      </div>

      <MerchantCreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        value={newMerchant}
        onChange={setNewMerchant}
        onSubmit={submitCreate}
        submitting={creating}
      />

      <MerchantCreatedResultModal result={createdResult} onClose={() => setCreatedResult(null)} />

      <MerchantSimpleModal
        open={showStatus && !!selectedMerchant}
        onClose={() => setShowStatus(false)}
        title={`Cambiar Estado: ${selectedMerchant?.company_name || ''}`}
        onConfirm={submitStatus}
      >
        <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="w-full px-3 py-2 border rounded-lg mb-4">
          <option value="active">Activo</option>
          <option value="suspended">Suspendido</option>
          <option value="pending">Pendiente</option>
        </select>
      </MerchantSimpleModal>

      <MerchantSimpleModal
        open={showFee && !!selectedMerchant}
        onClose={() => { setShowFee(false); setFeeReason(''); }}
        title={`Ajustar Comision: ${selectedMerchant?.company_name || ''}`}
        onConfirm={submitFee}
      >
        <input
          type="number" step="0.1" value={newFee}
          onChange={(e) => setNewFee(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg mb-2" placeholder="Porcentaje"
        />
        <input
          type="text" value={feeReason}
          onChange={(e) => setFeeReason(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg mb-4" placeholder="Razon (opcional)"
        />
      </MerchantSimpleModal>

      <MerchantSimpleModal
        open={showTolerance && !!selectedMerchant}
        onClose={() => { setShowTolerance(false); setToleranceReason(''); }}
        title={`Tolerancia de sobrepago: ${selectedMerchant?.company_name || ''}`}
        onConfirm={submitTolerance}
        testid="tolerance-modal"
      >
        <p className="text-xs text-gray-500 mb-3">
          Si el cliente paga hasta este monto <strong>por encima</strong> del total de la orden,
          la orden se marca pagada y se acredita al ledger el monto <strong>real recibido</strong>.
          Pagar de menos sigue fallando. Dejar en 0 o vacío desactiva la tolerancia.
        </p>
        <input
          type="number" step="1" min="0" value={newTolerance}
          data-testid="tolerance-input"
          onChange={(e) => setNewTolerance(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg mb-2" placeholder="Bolivares (ej. 100)"
        />
        <input
          type="text" value={toleranceReason}
          onChange={(e) => setToleranceReason(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg mb-4" placeholder="Razon (opcional)"
        />
      </MerchantSimpleModal>

      <MerchantSimpleModal
        open={showPassword && !!selectedMerchant}
        onClose={() => { setShowPassword(false); setNewPassword(''); }}
        title={`Resetear Contrasena: ${selectedMerchant?.company_name || ''}`}
        onConfirm={submitPassword}
        confirmText="Resetear"
        confirmTone="danger"
      >
        <input
          type="text" value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg mb-4"
          placeholder="Nueva contrasena (min 8 caracteres)"
        />
      </MerchantSimpleModal>

      <MerchantProjectsModal
        open={showProjects}
        onClose={() => setShowProjects(false)}
        merchant={selectedMerchant}
      />

      <MerchantConcentratorModal
        open={showConcentrator}
        onClose={() => setShowConcentrator(false)}
        merchant={selectedMerchant}
        value={concentrator}
        onChange={setConcentrator}
        onSave={submitConcentrator}
        saving={savingConcentrator}
      />

      <MerchantDeleteModal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        merchant={selectedMerchant}
        action={deleteAction}
        impact={deleteImpact}
        confirmName={deleteConfirmName} setConfirmName={setDeleteConfirmName}
        reason={deleteReason} setReason={setDeleteReason}
        override={deleteOverride} setOverride={setDeleteOverride}
        onConfirm={submitDelete}
        deleting={deleting}
      />
    </div>
  );
}
